import { VideoCameraIcon } from "@heroicons/react/24/outline"
import React, { useEffect, useMemo } from "react"

import { useManualBooking } from "../../../../contexts/ManualBookingContext"
import { usePractice } from "../../../../contexts/PracticeContext"
import { Label, Select } from "../../../shared/Inputs"

const selectedServiceIds = (services) =>
  (services || [])
    .map((row) => row.service?.serviceId || row.service?.id)
    .filter(Boolean)
    .map(String)

const LocationSelect = () => {
  const { practice } = usePractice()
  const { appointment, locationId, setLocationId, services } = useManualBooking()

  const locationOptions = useMemo(() => {
    const all = practice?.locations || []
    const selectedIds = selectedServiceIds(services)
    if (selectedIds.length === 0) {
      return all.map((location) => ({ label: location.singleLine, value: location.id }))
    }

    const allowed = new Set()
    ;(practice?.services || []).forEach((service) => {
      if (!selectedIds.includes(String(service.id))) return
      ;(service.locations || []).forEach((location) => allowed.add(String(location.id)))
    })
    if (allowed.size === 0) {
      all.forEach((location) => {
        const offered = (location.services || []).some((service) => selectedIds.includes(String(service.id)))
        if (offered) allowed.add(String(location.id))
      })
    }

    const visible = allowed.size > 0 ? all.filter((location) => allowed.has(String(location.id))) : all
    return visible.map((location) => ({ label: location.singleLine, value: location.id }))
  }, [practice, services])

  useEffect(() => {
    if (locationOptions.length === 0) return
    const values = locationOptions.map((option) => String(option.value))
    if (!locationId || !values.includes(String(locationId))) {
      setLocationId(locationOptions[0].value)
    }
  }, [locationId, locationOptions, setLocationId])

  const virtualLink = appointment?.virtualMeetingLinkWithFallback

  return (
    <div className="mt-4">
      <Label htmlFor="location-select">Location</Label>
      <Select
        id="location-select"
        options={locationOptions}
        onChange={(e) => setLocationId(e.target.value)}
        value={locationId || undefined}
      />
      {appointment && virtualLink && (
        <div className="mt-2 text-sm">
          <VideoCameraIcon className="mr-2 inline size-4" />
          <a href={virtualLink} target="_blank" rel="noopener noreferrer" className="text-teal">
            Join virtual meeting
          </a>
        </div>
      )}
    </div>
  )
}

export default LocationSelect
